import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Button, Message, Icon, Header, Modal, Form, TextArea,
} from 'semantic-ui-react';
import PROJECT, { GENERATE_PROJECT_REPORT } from '../../../queries/project';

// Mirrors MAX_REPORT_DIRECTIVE_LENGTH in api/src/ai/googleVertexAI/createProjectReport.ts,
// which is the value actually enforced server-side — keep the two in sync.
const MAX_DIRECTIVE_LENGTH = 5000;

const GenerateProjectReport = ({ project }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [directive, setDirective] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  // Generation is kicked off in the background, so the `project` prop only
  // reflects the new "initiating"/"running" status once the refetch triggered
  // below round-trips. Track it locally too so the spinner appears the instant
  // the mutation succeeds, instead of waiting on that refetch.
  const [justStarted, setJustStarted] = useState(false);

  const [generateProjectReport, { loading }] = useMutation(GENERATE_PROJECT_REPORT, {
    refetchQueries: [{ query: PROJECT, variables: { id: project.id } }],
    onCompleted: () => {
      setModalOpen(false);
      setDirective('');
      setErrorMessage(null);
      setJustStarted(true);
    },
    onError: (error) => {
      setErrorMessage(error.message);
    },
  });

  const generationStatus = project?.aiReport?.generationStatus;
  const generationError = project?.aiReport?.generationError;
  const hasReport = (project?.aiReport?.sections || []).length > 0;
  const isGenerating = justStarted || generationStatus === 'initiating' || generationStatus === 'running';

  const openModal = () => {
    setErrorMessage(null);
    // Prefill with any directive already saved on the project so confirming without
    // retyping re-sends it instead of dropping it. The generateProjectReport mutation
    // overwrites aiReport.directive with whatever directive is provided.
    setDirective(project?.aiReport?.directive || '');
    setModalOpen(true);
  };

  const handleGenerate = async () => {
    await generateProjectReport({
      variables: { projectId: project.id, directive: directive.trim() || undefined },
    });
  };

  const handleRetry = async () => {
    await generateProjectReport({
      variables: { projectId: project.id, directive: project?.aiReport?.directive || undefined },
    });
  };

  const renderDirectiveModal = () => (
    <Modal
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      closeIcon
      size="small"
    >
      <Modal.Header>
        {hasReport ? 'Regenerate report' : 'Generate project report'}
      </Modal.Header>
      <Modal.Content>
        {hasReport && (
          <Message warning>
            <Message.Header>This will overwrite the existing report</Message.Header>
            <p>
              A newly generated report based on this project&apos;s current data will
              replace it. The current report cannot be recovered afterward.
            </p>
          </Message>
        )}
        <p>
          This synthesizes all transcripts and screening data for this project into a
          structured report.
        </p>
        <Form>
          <Form.Field
            control={TextArea}
            label="Directive for the AI (optional)"
            placeholder="e.g. Also assess whether pricing sensitivity differs by region, and call out any regulatory concerns experts raised."
            value={directive}
            onChange={(_event, { value }) => setDirective(value.slice(0, MAX_DIRECTIVE_LENGTH))}
            maxLength={MAX_DIRECTIVE_LENGTH}
            rows={4}
          />
        </Form>
        <p className="text-muted">
          {'If provided, the report will include one or more extra sections addressing '}
          {'this directive, in addition to the standard report sections. '}
          {`${directive.length} / ${MAX_DIRECTIVE_LENGTH} characters.`}
        </p>
        {errorMessage && (
          <Message negative>
            <Message.Header>Error</Message.Header>
            <p>{errorMessage}</p>
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => setModalOpen(false)} disabled={loading}>
          Cancel
        </Button>
        <Button
          positive
          loading={loading}
          disabled={loading}
          onClick={handleGenerate}
        >
          {hasReport ? 'Confirm & Regenerate' : 'Confirm & Generate'}
        </Button>
      </Modal.Actions>
    </Modal>
  );

  const renderContent = () => {
    if (project.numCompleted < 1) {
      return (
        <Message warning>
          <Message.Header>Not enough data yet</Message.Header>
          <p>This project needs at least one completed job before a report can be generated.</p>
        </Message>
      );
    }

    if (isGenerating) {
      return (
        <Message icon info>
          <Icon name="circle notched" loading />
          <Message.Content>
            <Message.Header>Generating project report&hellip;</Message.Header>
            This may take several minutes. Refresh the page to check for updates.
          </Message.Content>
        </Message>
      );
    }

    if (generationStatus === 'failed') {
      return (
        <>
          <Message negative>
            <Message.Header>Report generation failed</Message.Header>
            <p>{generationError || 'An error occurred while generating the report.'}</p>
          </Message>
          <Button primary loading={loading} disabled={loading} onClick={handleRetry}>
            Retry
          </Button>
          {renderDirectiveModal()}
        </>
      );
    }

    if (hasReport) {
      return (
        <>
          <Message positive>
            <Message.Header>Report available</Message.Header>
            <p>
              {'View it in the '}
              <strong>Report</strong>
              {' tab.'}
            </p>
            <Button onClick={openModal}>
              Regenerate Report
            </Button>
          </Message>
          {renderDirectiveModal()}
        </>
      );
    }

    return (
      <>
        <Button primary onClick={openModal}>
          Generate Project Report
        </Button>
        {renderDirectiveModal()}
      </>
    );
  };

  return (
    <div style={{ marginTop: '1em' }}>
      <Header as="h4">Generate Project Report</Header>
      {renderContent()}
    </div>
  );
};

export default GenerateProjectReport;
